/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { jsx, css } from '@emotion/react';
import { NavigationalButtons } from "./CreateAudioWizard";
import { Form, Formik, useFormikContext } from "formik";
import useAxios from "axios-hooks";
import ChoiceGroup from "../../../../Forms/ChoiceGroup";
import { DatePicker } from "../../../../Forms/DatePicker";
import ComboBox from "../../../../Forms/ComboBox";
import TextField from "../../../../Forms/TextField";
import FluentSelect from "../../../../Forms/FluentSelect";
import {Spinner, Text, Separator} from "@fluentui/react";
import Toggle from "../../../../Forms/Toggle";

const cssError = css`
    color: red;
    font-size: 12px;
    margin-top: 5px;
`;

const audioAccessSchema = Yup.object({
  hasStandardRules: Yup.boolean().required(),
  standardInclusionMode: Yup.string()
    .nullable()
    .when("hasStandardRules", {
      is: true,
      then: Yup.string()
        .oneOf(["IncludeOnly", "ExcludeOnly", "All"])
        .required("Please select a rule for standard access"),
      otherwise: Yup.string().oneOf(["None"]),
    }),
  standardCountries: Yup.array().of(Yup.string())
    .when(["hasStandardRules", "standardInclusionMode"], {
      is: (hasRules, mode) => hasRules && mode !== "All",
      then: Yup.array()
        .min(1, "Please select at least one country for standard rules")
        .required(),
      otherwise: Yup.array().notRequired(),
    }),
  hasLocalizedRules: Yup.boolean().required(),
  localizedInclusionMode: Yup.string()
    .nullable()
    .when("hasLocalizedRules", {
        is: true,
        then: Yup.string()
        .oneOf(["IncludeOnly", "ExcludeOnly", "All"])
        .required("Please select a rule for localized access"),
        otherwise: Yup.string().oneOf(["None"]),
    }),
  localizedCountries: Yup.array().of(Yup.string())
    .when(["hasLocalizedRules", "localizedInclusionMode"], {
      is: (hasRules, mode) => hasRules && mode !== "All",
      then: Yup.array()
        .min(1, "Please select at least one country for localized rules")
        .required(),
      otherwise: Yup.array().notRequired(),
    }),
});

const AuditStep = ({nextOnClick, prevOnClick, audioId, audio}) => {
    const [{ loading: loading, data: localization }] = useAxios(
        {
            url: `resources/audio/${audioId}/localization`,
            method: "get",
        }
    );

    const [{ data: countries, loading: loadingCountries }] = useAxios(
        `/resources/audio/${audioId}/countries`
    );

    const [{ loading: submitting }, postForm] = useAxios(
        {
            url: `resources/audio/${audioId}/audit`,
            method: "put",
        },
        { manual: true }
    );

    if (!audio || loading || submitting || loadingCountries) {
        return (
            <Spinner className="absolute-center" label="Loading..." />
        );
    }

    return (
        <Formik
            validationSchema={audioAccessSchema}
            initialValues={{
                publishStatus: audio.publishStatus,
                auditOwner: audio.auditOwner || '',
                evergreenStatus: audio.evergreenStatus, 
                evergreenReviewDate: audio.evergreenReviewDate, 
                evergreenExpireDate: audio.evergreenExpireDate, 
                seasonalStatus: audio.seasonalStatus.toString(),
                seasonalStartDate: audio.seasonalStartDate,
                seasonalEndDate: audio.seasonalEndDate,
                evergreenComments: audio.evergreenComments,
                isGlobal: audio.isGlobal.toString(),
                hasLocalizedRules: audio.hasLocalizedRules, 
                hasStandardRules: audio.hasStandardRules,
                localizationCountries: localization,
                standardCountries: localization.standardCountries || [],
                localizedCountries: localization.localizedCountries || [],
                standardInclusionMode: localization.standardInclusionMode || 'None',
                localizedInclusionMode: localization.localizedInclusionMode || 'None',
            }}

        onSubmit={async (values, { setErrors }) => {
            if (
            values.publishStatus === "PB" &&
            !values.hasStandardRules &&
            !values.hasLocalizedRules
                ) {
                setErrors({
                    hasStandardRules: "At least one rule (standard or localized) must be defined when publishing.",
                    hasLocalizedRules: "At least one rule (standard or localized) must be defined when publishing.",
                });
                return;
            }
            const cleaned = { ...values };
            if (cleaned.evergreenStatus != "Regular Review") {
                cleaned.evergreenReviewDate = null;
            }
            if (cleaned.evergreenStatus != "News") {
                cleaned.evergreenExpireDate = null;
            }
            if (cleaned.seasonalStatus != "1") {
                cleaned.seasonalStartDate = null;
                cleaned.seasonalEndDate = null;
            }

            const {
                included: standardIncludedCountries,
                excluded: standardExcludedCountries
            } = determineSplit(cleaned.standardCountries, cleaned.standardInclusionMode);

            const {
                included: localizedIncludedCountries,
                excluded: localizedExcludedCountries
            } = determineSplit(cleaned.localizedCountries, cleaned.localizedInclusionMode);

            cleaned.CountryAccess = {
                HasStandardRules: cleaned.hasStandardRules,
                HasLocalizedRules: cleaned.hasLocalizedRules,
                StandardIncludedCountries: standardIncludedCountries,
                StandardExcludedCountries: standardExcludedCountries,
                LocalizedIncludedCountries: localizedIncludedCountries,
                LocalizedExcludedCountries: localizedExcludedCountries,
                StandardInclusionMode: cleaned.standardInclusionMode,
                LocalizedInclusionMode: cleaned.localizedInclusionMode
            };

            const result = await postForm({ data: cleaned });

            if (!result.isAxiosError) {
                nextOnClick();
            }

        }}
        >
        { props => (
            <AuditStepForm 
                prevOnClick={prevOnClick} 
                submitting={submitting} 
                audio={audio} 
                values={props.values} 
                countries={countries} 
                errors={props.errors}
                touched={props.touched}
            />
        )}
    </Formik>
    );
}


const AuditStepForm = ({values, prevOnClick, submitting, countries, errors, touched}) => {
    const { setFieldValue } = useFormikContext();
    const statusOptions = [
        { key: "Evergreen", text: "Evergreen" },
        { key: "Expert Review", text: "Expert Review" },
        { key: "Regular Review", text: "Regular Review" },
        { key: "News", text: "News" },
      ];

    const seasonalStatusOptions = [
        { key: "0", text: "No Seasonal Reference(s)" },
        { key: "1", text: "Contains Sesonal Reference(s)" },
      ];

    const ownerOptions = [
        { key: "Content", text: "Content" },
        { key: "Product", text: "Product"},
        { key: "IT", text: "IT" },
        { key: "Client Success", text: "Client Success"},
        { key: "Marketing", text: "Marketing"} 
      ];

    const localizedOptions = [
        { key: "IncludeOnly", text: "Show To" },
        { key: "ExcludeOnly", text: "Hide From" },
        { key: "All", text: "Show To All" }
      ];
    const standardOptions = [
        { key: "IncludeOnly", text: "Show To" },
        { key: "ExcludeOnly", text: "Hide From" },
        { key: "All", text: "Show To All" },
      ];

    const showStandardInclusionOptions = values.hasStandardRules
        ? standardOptions.filter(o => o.key !== "None")
        : standardOptions;
    const showLocalizedInclusionOptions = values.hasLocalizedRules
        ? localizedOptions.filter(o => o.key !== "None")
        : localizedOptions;

    const countryOptions = countries && countries.map(x => {
        var obj = {value: x.country, label: x.name}
        return obj;
    });

    useEffect(() => {
        if (!values.hasStandardRules) {
            setFieldValue("standardCountries", []);
            setFieldValue("standardExcludedCountries", []);
            setFieldValue("standardInclusionMode", "None");
        }
    }, [values.hasStandardRules, setFieldValue]);

    useEffect(() => {
        if (!values.hasLocalizedRules) {
            setFieldValue("localizedCountries", []);
            setFieldValue("localizedExcludedCountries", []);
            setFieldValue("localizedInclusionMode", "None");
        }
    }, [values.hasLocalizedRules, setFieldValue]);

    useEffect(() => {
        if (values.standardInclusionMode === "All") {
            setFieldValue("standardCountries", []);
            setFieldValue("standardExcludedCountries", []);
        }
    }, [values.standardInclusionMode, setFieldValue]);

    useEffect(() => {
        if (values.localizedInclusionMode === "All") {
            setFieldValue("localizedCountries", []);
            setFieldValue("localizedExcludedCountries", []);
        }
    }, [values.localizedInclusionMode, setFieldValue]);


    return (
            <Form>
            <div>
                <ChoiceGroup
                    label={"Audit Status"}
                    options={statusOptions}
                    name="evergreenStatus"
                    required
                />
                {values.evergreenStatus == "Regular Review" &&
                    <DatePicker label="Review Date" name="evergreenReviewDate" allowTextInput required/>
                }
                {values.evergreenStatus == "News" &&
                    <DatePicker label="Expiring Date" name="evergreenExpireDate" allowTextInput required/>
                }

                <ComboBox
                        label="Audit Owner"
                        placeholder="Select Team"
                        name="auditOwner"
                        options={ownerOptions}
                        style={{ width: 600 }}
                        autoComplete="on"
                    />
                <TextField label="Audit Comments" name="evergreenComments" />

                <ChoiceGroup
                    label={"Sesonal Status"}
                    options={seasonalStatusOptions}
                    name="seasonalStatus"
                    required
                />
                {values.seasonalStatus == "1" &&
                <>
                    <DatePicker label="Seasonal Start Date" name="seasonalStartDate" allowTextInput required/>
                    <DatePicker label="Seasonal End Date" name="seasonalEndDate" allowTextInput required/>
                </>
                }

                <Separator />
                <div className="mb20">
                    <Toggle
                        name="hasStandardRules"
                        label="Standard Classification Rules"
                        onText="Audio has standard rules."
                        offText="Audio does not have standard rules"
                    />
                    <Text variant="small">
                        The audio content is {values.hasStandardRules ? "general and unpaid unless rules are defined below to restrict its visibility." : "not considered standard."}
                    </Text>
                    {touched.hasStandardRules && errors.hasStandardRules && (
                        <div css={cssError}>{errors.hasStandardRules}</div>
                    )}
                    {values && values.hasStandardRules &&
                    <>
                        <ChoiceGroup
                            label={"Standard Country Access Rules"}
                            options={showStandardInclusionOptions}
                            name="standardInclusionMode"
                        />
                        {touched.hasStandardRules && errors.standardInclusionMode && (
                            <div css={cssError}>{errors.standardInclusionMode}</div>
                        )}
                        {values && values.standardInclusionMode && values.standardInclusionMode !== "All" &&
                            <>
                                <FluentSelect
                                    label={values.standardInclusionMode === "ExcludeOnly" ? "Hide From" : "Show To"}
                                    options={countryOptions}
                                    isMulti
                                    name='standardCountries'
                                    disabled={values.standardInclusionMode === 'All'}
                                />

                                {touched.hasStandardRules && errors.standardCountries && (
                                    <div css={cssError}>{errors.standardCountries}</div>
                                )}
                            </>
                        }
                    </>
                    }
                </div>
                <Toggle
                    name="hasLocalizedRules"
                    label="Localized Classification Rules"
                    onText="Audio has localized rules."
                    offText="Audio does not have localized rules"
                />
                <Text variant="small">
                    The audio content is {values.hasLocalizedRules ? "paid and restricted. Use the rules below to define the locations where it should appear." : "not considered localized."}
                </Text>
                {touched.hasLocalizedRules && errors.hasLocalizedRules && (
                    <div css={cssError}>{errors.hasLocalizedRules}</div>
                )}
                {values && values.hasLocalizedRules &&
                <>
                    <ChoiceGroup
                        label={"Localized Country Access Rules"}
                        options={showLocalizedInclusionOptions}
                        name="localizedInclusionMode"
                    />
                    {touched.hasLocalizedRules && errors.localizedInclusionMode && (
                        <div css={cssError}>{errors.localizedInclusionMode}</div>
                    )}

                    {values && values.localizedInclusionMode && values.localizedInclusionMode !== "All" &&
                        <>
                            <FluentSelect
                                label={values.localizedInclusionMode === "ExcludeOnly" ? "Hide From" : "Show To"}
                                options={countryOptions}
                                isMulti
                                name='localizedCountries'
                                disabled={values.localizedInclusionMode === 'All'}
                            />
                            {touched.hasLocalizedRules && errors.localizedCountries && (
                                <div css={cssError}>{errors.localizedCountries}</div>
                            )}
                        </>
                    }
                </>
                }
                
                <NavigationalButtons
                    loading={submitting}
                    onNextProps={{ type: "submit", text: "Save and Continue" }}
                    onPrevHandler={prevOnClick}
                />
            </div>
            </Form>);
}

const determineSplit = (countries, mode) => {
    if (mode === "IncludeOnly") {
      return { included: countries, excluded: [] };
    }
    if (mode === "ExcludeOnly") {
      return { included: [null], excluded: countries };
    }
    if (mode === "All") {
      return { included: [null], excluded: [] };
    }
    return { included: [], excluded: [] }; 
  };

export default AuditStep;